import clsx from "clsx";
import { type KeenSliderOptions, useKeenSlider } from "keen-slider/react";
import { useLocaleInfo } from "../lib/hooks/useLocaleInfo";
import "keen-slider/keen-slider.min.css";

export type SliderProps<ItemShape> = {
  items: ItemShape[];
  renderItem: (item: ItemShape, index: number) => React.ReactNode;
  getItemKey?: (item: ItemShape, index: number) => string | number;
  slideClassName?: string;
} & Partial<KeenSliderOptions>;

export function Slider<ItemShape>(props: SliderProps<ItemShape>) {
  const {
    items,
    renderItem,
    getItemKey,
    slideClassName,
    loop = true,
    renderMode = "performance",
    slides = { perView: "auto", spacing: 8, origin: "center" },
    disabled = items.length === 0,
    ...restSliderOptions
  } = props;
  const { dir } = useLocaleInfo();

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop,
    slides,
    renderMode,
    rtl: dir === "rtl",
    disabled,
    ...restSliderOptions,
  });

  if (items.length === 0) return null;

  return (
    <div ref={sliderRef} className="keen-slider" data-testid="slider">
      {(loop && items.length === 2 ? [...items, ...items] : items).map(
        (item, index) => (
          <div
            key={getItemKey ? getItemKey(item, index) : index}
            className={clsx("keen-slider__slide", slideClassName)}
            data-testid="slider-item"
          >
            {renderItem(item, index)}
          </div>
        ),
      )}
    </div>
  );
}
